import styled from 'styled-components';
import { allProps, Box, useComponentStyle } from '@altermaind/web/ui';
import { useSubComponentStyle } from '@/_shared/hooks';
import {
  ParseIsDisabledProps,
  ParseTranslateXProps,
  StyledSwitchProps,
  SwitchThumbProps,
} from './types';

const parseIsDisabled = ({ theme, isDisabled }: ParseIsDisabledProps) => {
  if (isDisabled) {
    return allProps({
      theme,
      cursor: 'not-allowed',
      opacity: 0.4,
    });
  }
  return allProps({
    theme,
    cursor: 'pointer',
    opacity: 1,
  });
};

/**
 * Using styled component instead of Box to handle thumb subcomponent with the variants
 * theme styles will have priority over passed props, for instance custom backgroundColor will not have effect because theme will override it
 * props don't used in theme (like m, p, etc.) will be applied normally
 */
export const StyledSwitch = styled(Box)<StyledSwitchProps>(
  allProps,
  ({ theme, variant }) => {
    const themeStyle = useComponentStyle('Switch', variant);
    return allProps({ theme, ...themeStyle });
  },
  /**
   * If need to override theme styles, define custom parser and use it here
   * @example
   * ({ theme, backgroundColor }) => allProps({ theme, backgroundColor }),
   */
  parseIsDisabled,
);
StyledSwitch.defaultProps = {
  role: 'switch',
};

const parseTranslateX = ({ theme, translateX }: ParseTranslateXProps) =>
  allProps({
    theme,
    transform: `translateX(${translateX}px)`,
  });

/**
 * theme styles will have priority over passed props, for instance custom backgroundColor will not have effect because theme will override it
 * props don't used in theme (like m, p, etc.) will be applied normally
 */
export const SwitchThumb = styled(Box)<SwitchThumbProps>(
  ({ theme, variant, size }) => {
    // DS components don't manage subcomponents directly, so we're using custom useSubComponentStyle hook
    const { thumb } = useSubComponentStyle('Switch', variant);
    return allProps({
      theme,
      ...thumb,
      height: size,
      width: size,
    });
  },
  /**
   * If need to override theme styles, define custom parser and use it here
   * @example
   * ({ theme, backgroundColor }) => allProps({ theme, backgroundColor }),
   */
  parseTranslateX,
);
